"use client";
import Link from "next/link";
import Image from "next/image";
import { X, ChevronRight, User, HelpCircle } from "lucide-react";
import { Button } from "@/app/components/shadcn/button";
import Logo from "@/app/components/elements/Logo";
import FallbackImage from "@/app/components/elements/FallbackImage";
import Search from "@/app/components/elements/forms/search/Search";
import useAuth from "@/app/lib/features/auth/auth.hook";
import { MenuItem } from "@/app/lib/features/menus/menus.types";
import { trackInteraction } from "@/app/lib/tracking/interactions";

/**
 * Alt kategorisi olan başlık detay görünümünü açar; olmayan başlık ise detayda
 * yalnızca "Alt kategori bulunamadı" gösteriyordu — o yüzden doğrudan kendi
 * sayfasına link olur. `children` API'den `[]` gelebiliyor, length ile bakılıyor.
 */
const hasChildren = (cat: MenuItem) => Boolean(cat.children?.length);

interface MobileNavListViewProps {
  menus: MenuItem[];
  isLoading: boolean;
  onClose: () => void;
  onCategoryClick: (id: number) => void;
}

const MobileNavListView = ({
  menus,
  isLoading,
  onClose,
  onCategoryClick,
}: MobileNavListViewProps) => {
  const { isAuthenticated } = useAuth();

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between h-15.75 bg-white px-4 border-b shrink-0">
        <Logo className="w-30 sm:w-40" />
        <Button variant="outline" size="icon-sm" onClick={onClose}>
          <X />
        </Button>
      </div>

      {/* Search */}
      <div className="bg-white pt-3 pb-2 shrink-0">
        <div className="px-4">
          <Search onPhotoSearchOpen={onClose} />
        </div>
      </div>

      {/* Flash banner */}
      <Link
        href="/kadin"
        onClick={onClose}
        className="w-full flex bg-linear-to-l text-white px-4 from-[#ff488b] to-[#ff6d13] items-center justify-between h-13 shrink-0"
      >
        <div className="flex items-center gap-3">
          <Image
            src="/images/icons/double-lightning-icon.svg"
            alt="Flaş"
            width={32}
            height={32}
          />
          <div>
            <p className="text-sm">Flaş Ürünler</p>
            <p className="text-xs">Hemen al, fırsatı kaçırma!</p>
          </div>
        </div>
        <div className="w-4 h-4 bg-white rounded-full flex items-center justify-center">
          <ChevronRight className="text-muted-foreground" size={18} />
        </div>
      </Link>

      {/* Category Grid */}
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-2 gap-3 p-4">
          {isLoading || !menus.length ? (
            Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-22 rounded-2xl bg-gray-200 animate-pulse" />
            ))
          ) : (
            menus.map((cat) => {
              const cardClass =
                "h-22 bg-white rounded-2xl shadow-sm overflow-hidden flex items-stretch text-left";
              const content = (
                <>
                  <div className="flex-1 px-3 flex flex-col justify-center gap-1 min-w-0">
                    <span className="font-semibold text-sm leading-tight line-clamp-2">
                      {cat.name}
                    </span>
                    {cat.badge_text && (
                      <span className="text-[10px] font-medium text-white bg-main rounded-full px-2 py-0.5 w-fit">
                        {cat.badge_text}
                      </span>
                    )}
                  </div>
                  <div className="w-[38%] relative shrink-0 self-stretch">
                    <FallbackImage src={cat.image_url} alt={cat.name} fill className="object-cover" />
                  </div>
                </>
              );

              // Alt kategorisi yoksa detay görünümü boş kalırdı — doğrudan git.
              if (!hasChildren(cat) && cat.url) {
                return (
                  <Link
                    key={cat.id}
                    href={`/${cat.url}`}
                    onClick={() => { trackInteraction("menu", cat.id, "click"); onClose(); }}
                    className={cardClass}
                  >
                    {content}
                  </Link>
                );
              }

              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => { trackInteraction("menu", cat.id, "click"); onCategoryClick(cat.id); }}
                  className={cardClass}
                >
                  {content}
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white divide-y shrink-0">
        {!isAuthenticated && (
          <Link
            href="/auth/login"
            onClick={onClose}
            className="flex items-center gap-3 px-4 h-13 hover:bg-gray-50 transition-colors"
          >
            <User className="text-muted-foreground" size={20} />
            <p className="text-sm font-medium text-gray-700">Kayıt Ol / Giriş Yap</p>
          </Link>
        )}
        <Link
          href="/help"
          onClick={onClose}
          className="flex items-center gap-3 px-4 h-13 hover:bg-gray-50 transition-colors"
        >
          <HelpCircle className="text-muted-foreground" size={20} />
          <p className="text-sm font-medium text-gray-700">Yardım</p>
        </Link>
        <div className="px-4 py-3">
          <Link href="/cerez-tercihlerimi-yonet" onClick={onClose} className="text-muted-foreground underline text-sm">
            Çerez Tercihlerim
          </Link>
        </div>
      </div>
    </>
  );
};

export default MobileNavListView;